import { ITeam } from 'types/team';

export interface ITeamMemberFormRow {
  value: string;
  function_id?: string | null;
  members_team_id?: string;
}

type MembersTeamsAttribute = {
  id?: string | number;
  member_id?: string;
  function_id?: string | null;
  _destroy?: 1;
};

/** Nested attributes dla members_teams — ten sam wzorzec co task_participations w TaskTable. */
export function buildMembersTeamsAttributes(
  existing: ITeam['members_teams'],
  selected: ITeamMemberFormRow[] | undefined
): MembersTeamsAttribute[] {
  if (!existing?.length) {
    return (
      selected?.map((member) => ({
        member_id: member.value,
        ...(member.function_id ? { function_id: member.function_id } : {})
      })) ?? []
    );
  }

  const selectedByMemberId = new Map(
    (selected ?? []).map((member) => [String(member.value), member])
  );

  const removed = existing
    .filter((row) => row.id != null && !selectedByMemberId.has(String(row.member_id)))
    .map((row) => ({ id: row.id!, _destroy: 1 as const }));

  const added = (selected ?? [])
    .filter(
      (member) =>
        !existing.some((row) => String(row.member_id) === String(member.value))
    )
    .map((member) => ({
      member_id: member.value,
      ...(member.function_id ? { function_id: member.function_id } : {})
    }));

  const functionUpdates = existing
    .filter((row) => {
      const member = selectedByMemberId.get(String(row.member_id));
      if (!member || row.id == null) return false;

      const nextFunctionId = member.function_id ?? null;
      const currentFunctionId =
        row.function_id != null
          ? String(row.function_id)
          : row.function?.id != null
            ? String(row.function.id)
            : null;

      return String(nextFunctionId ?? '') !== String(currentFunctionId ?? '');
    })
    .map((row) => ({
      id: row.id!,
      function_id: selectedByMemberId.get(String(row.member_id))!.function_id ?? null
    }));

  return [...removed, ...added, ...functionUpdates];
}

/** Pomocnik UI: z members_teams robi prostą listę osób (import członków z zespołu w zadaniu itd.). */
export function getTeamMembers(team: Pick<ITeam, 'members_teams'>) {
  return (team.members_teams ?? [])
    .filter((row) => row.member)
    .map((row) => ({
      id: row.member.id ?? row.member_id,
      email: row.member.email,
      role: row.member.role,
      username: row.member.username,
      company_id: row.member.company_id,
      first_name: row.member.first_name,
      last_name: row.member.last_name,
      photo: row.member.photo,
      members_team_id: row.id,
      function_id: row.function_id ?? row.function?.id ?? null
    }));
}
